import { flexRender } from "@tanstack/react-table";
import classNames from "classnames";
import { Cell } from "PFComponents/table/table.types";
import { CSSProperties, ReactNode } from "react";

import { TableData } from "../table";
import css from "../table.module.scss";
import { TableCellContext } from "../table.types";

export type TableCellProps<TABLE_DATA extends TableData> = {
  cell: Cell<TABLE_DATA, unknown>;
  compact?: boolean;
  displayGrid?: boolean;
  disabled?: boolean;
  hideDistantRows?: boolean;
  rowIndex?: number;
  colIndex?: number;
  className?: string;
  style?: CSSProperties;
  // Used by the interactive table layer to inject the fill-handle widget.
  fillHandle?: ReactNode;
  // Used by the interactive table layer to inject the selection border overlay.
  selectionBorder?: ReactNode;
  additionalCellData?: Record<string, unknown>;
  isLastColumn?: boolean;
};

export const TableCell = <TABLE_DATA extends TableData>({
  cell,
  compact,
  displayGrid,
  hideDistantRows,
  rowIndex,
  colIndex,
  className,
  style,
  fillHandle,
  selectionBorder,
  additionalCellData
}: TableCellProps<TABLE_DATA>) => {
  const { align, sticky, cellClassName, getRowSpan } = cell.column.columnDef.meta || {};
  const rowSpan = getRowSpan && rowIndex !== undefined ? getRowSpan(rowIndex) : undefined;

  const cellContext: TableCellContext<TABLE_DATA> = { ...cell.getContext(), additionalCellData };

  return (
    <td
      className={classNames(cellClassName, className, {
        [css.compactCell]: compact,
        [css.alignRight]: align === "right",
        [css.sticky]: sticky,
        [css.displayGrid]: displayGrid,
        [css.visibleContent]: hideDistantRows
      })}
      style={{
        width: cell.column.getSize() !== 0 ? cell.column.getSize() : undefined,
        ...style
      }}
      data-row-index={rowIndex}
      data-col-index={colIndex}
      rowSpan={rowSpan}
    >
      {flexRender(cell.column.columnDef.cell, cellContext)}
      {selectionBorder}
      {fillHandle}
    </td>
  );
};
